import { JobStatus, PartFinderReport } from '@/api/generatedApi';
import { StyledListItem } from '@/components/AssetsPage/PopoverList';
import { SectionHeader } from '@/components/AssetsPage/SectionHeader';
import { BodyText } from '@/components/BodyText';
import { BaseContextMenu } from '@/components/ContextMenu/BaseContextMenu';
import { CopyMenuItem, getCopyValueFromColumn } from '@/components/ContextMenu/CopyMenuItem';
import { DataGridLazy } from '@/components/DataGrid/DataGridLazy';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { getBatchLabel } from '@/components/PartFinder/batchLabel';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useCancelPartFinderReports } from '@/hooks/api/part-finder-reports/useCancelPartFinderReports';
import { BulkDeleteReportsModal } from '@/components/PartFinder/BulkDeleteReportsModal';
import { BulkActionSidebar } from '@/components/PartFinder/BulkActionSidebar';
import { PartFinderExportButton } from '@/components/PartFinder/PartFinderExportButton';
import { CancelBatchModal } from '@/components/PartFinder/CancelBatchModal';
import { DeleteBatchModal } from '@/components/PartFinder/DeleteBatchModal';
import { isInProgress } from '@/components/PartFinder/activityRows';
import { partFinderReportColumns, partFinderThumbnailColumn } from '@/components/PartFinder/columnDefinitions';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { resolveContextTargetReportIds } from '@/components/PartFinder/reportSelection';
import { useToast } from '@/hooks/useToast';
import { useListBatchReports } from '@/hooks/api/part-finder-batches/useListBatchReports';
import { useContextMenu } from '@/hooks/useContextMenu';
import { usePartFinderBatchPolling } from '@/hooks/useDataPolling/usePartFinderBatchPolling';
import { usePartFinderExport } from '@/hooks/api/part-finder-reports/usePartFinderExport';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import { BlockOutlined, ChevronRight, DeleteOutlined, FileDownloadOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { format } from 'date-fns';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { EditableBatchName } from './components/EditableBatchName';

const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  overflowY: 'auto',
});

const ContentWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px 24px 24px 24px',
});

const ProgressBar = styled(LinearProgress)({ height: 8, borderRadius: 4 });

const ProgressSummary = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginTop: '6px',
}));

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
});

const GridArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  minWidth: 0,
  '& .MuiDataGrid-root': { borderRadius: '6px !important' },
  '& .MuiDataGrid-columnHeaders': { background: alpha(theme.palette.secondary.main, 0.1) },
  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
  '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
  '& .MuiDataGrid-cell:focus': { outline: 'none !important' },
}));

const isTerminal = (status: JobStatus): boolean =>
  status === JobStatus.COMPLETED || status === JobStatus.FAILED || status === JobStatus.CANCELLED;

export const PartFinderBatchPage = () => {
  const { id } = useParams<{ id: string }>();
  const { getPath } = useTenants();
  const navigate = useNavigate();
  const { permissions } = useGetCurrentUser();
  const { isPartFinderThumbnailsEnabled } = useFeatureFlags();
  const reportColumns = isPartFinderThumbnailsEnabled
    ? [partFinderThumbnailColumn, ...partFinderReportColumns]
    : partFinderReportColumns;
  const canModifyBatch = permissions?.canEditPartFinderReport ?? false;
  const gridApi = useGridApiRef();
  const { data, isLoading, error } = usePartFinderBatchPolling(id ?? '');
  const { fetchData } = useListBatchReports(id ?? '');

  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [cancellableReportIds, setCancellableReportIds] = useState<string[]>([]);
  const [reportIdsToDelete, setReportIdsToDelete] = useState<string[]>([]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { showToast } = useToast();
  const { exportToExcel } = usePartFinderExport();
  const cancelReports = useCancelPartFinderReports({
    onSuccess: () => showToast({ message: 'Cancelled reports', type: 'success' }),
    onError: message => showToast({ message, type: 'error' }),
  });

  const {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleGridContextMenu,
    handleCloseContextMenu,
  } = useContextMenu<PartFinderReport>({
    canUseContextMenu: true,
    getRowById: rowId => gridApi.current?.getRow(rowId) ?? undefined,
  });

  const contextMenuReport = contextMenuClickedItem;
  const contextTargetReportIds = resolveContextTargetReportIds(contextMenuReport, selectedReportIds);
  const copyValue = getCopyValueFromColumn(partFinderReportColumns, contextMenuReport, contextMenuClickedField);
  const hasCopyValue = !!copyValue && contextTargetReportIds.length === 1;
  const deleteLabel = contextTargetReportIds.length > 1 ? `Delete ${contextTargetReportIds.length} reports` : 'Delete';
  const exportLabel =
    contextTargetReportIds.length > 1 ? `Export ${contextTargetReportIds.length} reports to Excel` : 'Export to Excel';
  const singleCancellableReportIds =
    contextMenuReport && isInProgress(contextMenuReport.status) ? [contextMenuReport.id] : [];
  const cancelTargetReportIds = contextTargetReportIds.length > 1 ? cancellableReportIds : singleCancellableReportIds;
  const cancelLabel = cancelTargetReportIds.length > 1 ? `Cancel ${cancelTargetReportIds.length} reports` : 'Cancel';

  const batch = data?.partFinderBatch;
  usePageTitle(batch ? `Batch (${batch.breakdown.total})` : 'Batch');

  const fetchReports = async ({ page, perPage }: { page: number; perPage: number }) => {
    const response = await fetchData({ page, perPage });
    return { reports: response.partFinderReports, pageData: response.pageData };
  };

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    const all = (gridApi.current?.getAllRowIds() as string[] | undefined) ?? [];
    const ids =
      newSelection.type === 'exclude'
        ? all.filter(rowId => !newSelection.ids.has(rowId))
        : ([...newSelection.ids] as string[]);
    setSelectedReportIds(ids);
    setCancellableReportIds(
      ids.filter(rowId => {
        const row = gridApi.current?.getRow(rowId);
        return row !== undefined && isInProgress(row.status);
      })
    );
  };

  const clearSelection = () => {
    setSelectedReportIds([]);
    setCancellableReportIds([]);
    gridApi.current?.setRowSelectionModel({ type: 'include', ids: new Set() });
  };

  const handleDeleteComplete = () => {
    clearSelection();
  };

  const handleCancelComplete = () => {
    clearSelection();
  };

  const handleDeleteFromContextMenu = () => {
    if (contextTargetReportIds.length === 0) return;
    handleCloseContextMenu();
    setReportIdsToDelete(contextTargetReportIds);
  };

  const handleExportFromContextMenu = () => {
    if (contextTargetReportIds.length === 0) return;
    handleCloseContextMenu();
    void exportToExcel({ reportIds: contextTargetReportIds });
  };

  const handleCancelReportFromContextMenu = () => {
    if (cancelTargetReportIds.length === 0) return;
    handleCloseContextMenu();
    cancelReports.mutate(cancelTargetReportIds);
  };

  const handleDeleteFromContextComplete = () => {
    const wasBulk = reportIdsToDelete.length > 1;
    setReportIdsToDelete([]);
    if (wasBulk) handleDeleteComplete();
  };

  const header = (
    <SectionHeader>
      <Link to={getPath('part-finder')}>
        <BodyText>Part Finder</BodyText>
      </Link>
      {batch && (
        <>
          <ChevronRight sx={{ fontSize: '16px' }} />
          <BodyText>{getBatchLabel(batch)}</BodyText>
        </>
      )}
    </SectionHeader>
  );

  if (!id || (!isLoading && !batch)) {
    return (
      <PageContainer>
        {header}
        <ContentWrapper>
          <ApiErrorAlert error={error ? getApiError(error, 'Batch not found') : { message: 'Batch not found' }} />
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (isLoading || !batch) {
    return (
      <PageContainer>
        {header}
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </PageContainer>
    );
  }

  const { breakdown, status } = batch;
  const finishedCount = breakdown.completed + breakdown.failed + breakdown.cancelled;

  return (
    <PageContainer>
      {header}
      <ContentWrapper>
        <Box>
          <EditableBatchName batch={batch} />
          <Typography variant='body2' color='text.secondary'>
            Started {format(new Date(batch.createdAt), 'MMM d, yyyy HH:mm')}
            {batch.creator?.email && ` · by ${batch.creator.email}`}
          </Typography>
        </Box>
        {status === JobStatus.CANCELLED && (
          <Alert severity='warning'>This batch was cancelled. In-flight reports finished on their own.</Alert>
        )}
        {!isTerminal(status) && (
          <Box>
            <ProgressBar variant='determinate' value={batch.progress} />
            <ProgressSummary>
              {finishedCount} / {breakdown.total} finished
              {breakdown.running > 0 && ` · ${breakdown.running} running`}
              {breakdown.pending > 0 && ` · ${breakdown.pending} pending`}
              {breakdown.failed > 0 && ` · ${breakdown.failed} failed`}
            </ProgressSummary>
          </Box>
        )}
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              top: '-34px',
              right: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <PartFinderExportButton batchIds={[batch.id]} />
            {canModifyBatch && status === JobStatus.RUNNING && (
              <Tooltip title='Cancel batch'>
                <IconButton size='small' color='warning' aria-label='Cancel batch' onClick={() => setCancelOpen(true)}>
                  <BlockOutlined fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
            {canModifyBatch && (
              <Tooltip title='Delete batch'>
                <IconButton size='small' color='error' aria-label='Delete batch' onClick={() => setDeleteOpen(true)}>
                  <DeleteOutlined fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <GridArea>
            <DataGridLazy<PartFinderReport, { reports: PartFinderReport[] }>
              apiRef={gridApi}
              columns={reportColumns}
              initialState={{ pinnedColumns: { right: ['__check__'] } }}
              fetchRows={fetchReports}
              dataKey='reports'
              rowHeight={80}
              columnHeaderHeight={40}
              density='compact'
              checkboxSelection
              onRowSelectionModelChange={handleSelectionChange}
              disableRowSelectionOnClick
              disableColumnSorting
              autoHeight
              slotProps={{ row: { onContextMenu: handleGridContextMenu } }}
            />
            <BulkActionSidebar
              selectedReportIds={selectedReportIds}
              cancellableReportIds={cancellableReportIds}
              onDelete={handleDeleteComplete}
              onCancel={handleCancelComplete}
            />
          </GridArea>
        </Box>
      </ContentWrapper>
      {(hasCopyValue || contextMenuReport) && (
        <BaseContextMenu anchorPosition={contextMenuPosition} onClose={handleCloseContextMenu}>
          {hasCopyValue && contextMenuReport && (
            <CopyMenuItem
              columns={partFinderReportColumns}
              row={contextMenuReport}
              field={contextMenuClickedField}
              onCopy={handleCloseContextMenu}
            />
          )}
          {contextMenuReport && (
            <StyledListItem onClick={handleExportFromContextMenu}>
              <ListItemIcon>
                <FileDownloadOutlined fontSize='small' />
              </ListItemIcon>
              <ListItemText>{exportLabel}</ListItemText>
            </StyledListItem>
          )}
          {cancelTargetReportIds.length > 0 && (
            <StyledListItem onClick={handleCancelReportFromContextMenu}>
              <ListItemIcon>
                <BlockOutlined fontSize='small' />
              </ListItemIcon>
              <ListItemText>{cancelLabel}</ListItemText>
            </StyledListItem>
          )}
          {contextMenuReport && (
            <StyledListItem onClick={handleDeleteFromContextMenu}>
              <ListItemIcon>
                <DeleteOutlined fontSize='small' color='error' />
              </ListItemIcon>
              <ListItemText>{deleteLabel}</ListItemText>
            </StyledListItem>
          )}
        </BaseContextMenu>
      )}
      <BulkDeleteReportsModal
        open={reportIdsToDelete.length > 0}
        onClose={() => setReportIdsToDelete([])}
        selectedReportIds={reportIdsToDelete}
        onDeleteComplete={handleDeleteFromContextComplete}
      />
      <CancelBatchModal batch={batch} open={cancelOpen} onClose={() => setCancelOpen(false)} />
      <DeleteBatchModal
        batch={batch}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          navigate(getPath('part-finder'));
        }}
      />
    </PageContainer>
  );
};
